import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { PraxoraP4Panel } from '@/components/shared/PraxoraP4Panel';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { clientApi, FIT_VERDICT_LABEL, GAP_STATUS_TONE } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, ShieldCheck, FileDown, FileText, Sparkles, ArrowRight, CheckCircle2, ListChecks,
} from 'lucide-react';

// Governed correction notes are stored as "summary\n\n---\n**Label:** body".
// Render them as a styled note instead of raw Markdown.
const splitCorrectionNote = (text) => {
  const parts = String(text || '').split(/\n\n?-{3,}\n/);
  if (parts.length < 2) return { main: text, noteLabel: null, noteBody: null };
  const note = parts.slice(1).join('\n').trim();
  const m = note.match(/^\*\*(.+?)\*\*\s*([\s\S]*)$/);
  return {
    main: parts[0].trim(),
    noteLabel: m ? m[1].trim() : null,
    noteBody: (m ? m[2] : note).trim(),
  };
};

const SummaryWithNote = ({ text }) => {
  const { main, noteLabel, noteBody } = splitCorrectionNote(text);
  return (
    <>
      <p className="text-sm mt-4 leading-relaxed" data-testid="report-summary-text">{main}</p>
      {noteBody && (
        <div className="mt-4 rounded-lg border-l-4 border-warning bg-warning/5 px-4 py-3" data-testid="report-correction-note">
          {noteLabel && <p className="text-xs font-semibold uppercase tracking-wide text-warning">{noteLabel}</p>}
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{noteBody}</p>
        </div>
      )}
    </>
  );
};

export default function ClientReportDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let alive = true;
    clientApi.getReport(id)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const triggerDownload = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get(`/client/reports/${id}/export/${fmt}`, { responseType: 'blob' });
      const disp = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(disp);
      const filename = m ? m[1] : `CTH_Report.${fmt}`;
      // Preserve the server MIME type (a typeless Blob breaks DOCX saves in
      // Safari-class browsers) and defer revoke so the save can complete.
      const blob = res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: res.headers['content-type'] || '' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      toast.success(t('pages.clientReportDetail.downloaded', { fmt: fmt.toUpperCase() }));
    } catch (e) {
      toast.error(t('pages.clientReportDetail.downloadFailed'), { description: formatApiError(e) });
    } finally {
      setDownloading(null);
    }
  };
  const exportPdf = () => triggerDownload('pdf');
  const exportWord = () => triggerDownload('docx');

  if (loading) return (<><Topbar title={t('pages.clientReportDetail.title')} subtitle={t('pages.clientReportDetail.loadingSubtitle')} /><div className="p-12 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.clientReportDetail.loadingBody')}</div></>);
  if (error || !data) return (<><Topbar title={t('pages.clientReportDetail.title')} subtitle={t('pages.clientReportDetail.errorSubtitle')} /><div className="p-12 text-center"><p className="text-sm text-destructive">{error || t('pages.clientReportDetail.notFound')}</p><Button onClick={() => navigate('/client/reports')} className="mt-6">{t('pages.clientReportDetail.back')}</Button></div></>);

  const r = data.report;
  const v = data.version;
  const c = v?.content || {};
  const fit = r.fit;
  const sections = r.scores?.sections || [];
  const competencies = r.scores?.competencies || [];

  return (
    <>
      <Topbar title={data.candidate?.name || t('pages.clientReportDetail.title')} subtitle={t('pages.clientReportDetail.approvedSubtitle')} />
      <div className="p-6 lg:p-8 space-y-5 max-w-4xl mx-auto" data-testid="client-report-detail">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/reports')} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> {t('pages.clientReportDetail.backToReports')}</Button>
          <div className="flex gap-2">
            <Button data-testid="btn-client-export-pdf" variant="outline" size="sm" onClick={exportPdf} disabled={downloading !== null} className="gap-1.5">
              {downloading === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t('pages.clientReportDetail.downloadPdf')}
            </Button>
            <Button data-testid="btn-client-export-docx" variant="outline" size="sm" onClick={exportWord} disabled={downloading !== null} className="gap-1.5">
              {downloading === 'docx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t('pages.clientReportDetail.downloadDocx', { defaultValue: 'Word (DOCX)' })}
            </Button>
            {r?.suite_code_hint === 'talentcompetency' && (
              <Button data-testid="btn-client-export-responses" variant="ghost" size="sm" onClick={() => triggerDownload('responses-pdf')} disabled={downloading !== null} className="gap-1.5 text-muted-foreground border-l border-border rounded-none pl-3 ml-1">
                {downloading === 'responses-pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} {t('pages.clientReportDetail.downloadResponses', { defaultValue: 'Respuestas del cuestionario' })}
              </Button>
            )}
          </div>
        </div>

        <Card className="p-6 shadow-card">
          <Badge className="bg-success/10 text-success border-success/20"><ShieldCheck className="h-3 w-3 mr-1.5" /> {t('pages.clientReportDetail.approvedBadge')}</Badge>
          <h1 className="font-display text-2xl font-bold mt-3">{data.candidate?.name}</h1>
          <p className="text-sm text-muted-foreground" data-testid="report-detail-meta">
            {t('pages.clientReportDetail.released')} {new Date(r.released_at).toLocaleDateString(i18n.language)}
            {data.completed_at
              ? <> · {t('pages.clientReportDetail.completed', { defaultValue: 'Evaluation completed' })} {new Date(data.completed_at).toLocaleDateString(i18n.language)}</>
              : <> · <span className="italic" data-testid="report-detail-completed-na">{t('pages.clientReports.completedUnavailable', { defaultValue: 'The completion date is not available for this evaluation.' })}</span></>}
            {data.advisor_name ? <> · {t('pages.clientReportDetail.advisor', { defaultValue: 'Advisor' })}: {data.advisor_name}</> : null}
          </p>
          {!['praxora_p4', 'dimensional_profile'].includes(c.report_mode) && <SummaryWithNote text={c.summary} />}
        </Card>

        {['praxora_p4', 'dimensional_profile'].includes(c.report_mode) ? (
          <>
            <PraxoraP4Panel p4={c.p4} mode={c.report_mode} />
            {c.consultant_notes && (
              <Card className="p-6 shadow-card bg-muted/40">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t('pages.clientReportDetail.consultantNotes')}</p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.consultant_notes}</p>
              </Card>
            )}
          </>
        ) : (
        <>
        {/* Suitability */}
        {c.suitability && (
          <Card className="p-6 shadow-card">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.clientReportDetail.suitability')}</p>
            <div className="flex items-baseline gap-3 mt-2">
              <p className="text-4xl font-display font-bold">{c.suitability.score}</p>
              <Badge className="uppercase">{t(`reports.verdict.${c.suitability.verdict}`, { defaultValue: String(c.suitability.verdict).replace(/_/g, ' ') })}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{c.suitability.rationale}</p>
            <p className="text-xs text-muted-foreground/80 italic mt-3 leading-relaxed" data-testid="suitability-explainer">{t(fit?.source === 'competency_profile' ? 'pages.clientReportDetail.suitabilityExplainerTC' : r?.suite_code_hint === 'talenttrain' ? 'pages.clientReportDetail.suitabilityExplainerTT' : 'pages.clientReportDetail.suitabilityExplainer')}</p>
          </Card>
        )}

        {/* Role fit */}
        {fit && (
          <Card className="p-6 shadow-card" data-testid="client-fit-block">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.clientReportDetail.roleFit')}</p>
            <div className="flex items-baseline gap-3 mt-2">
              <p className="text-3xl font-display font-bold">{fit.fit_score}</p>
              <Badge variant="outline" className="uppercase">{t(`reports.verdict.${fit.verdict}`, { defaultValue: FIT_VERDICT_LABEL[fit.verdict] })}</Badge>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {fit.gaps?.map((g) => {
                const tone = GAP_STATUS_TONE[g.status] || GAP_STATUS_TONE.below;
                return (
                  <div key={g.competency_code} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{g.competency_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${tone.bg} ${tone.text} tabular-nums`}>{g.actual} / {g.expected}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Sections + competencies */}
        {(sections.length || competencies.length) > 0 && (
          <div className="grid md:grid-cols-2 gap-4">
            {sections.length > 0 && (
              <Card className="p-6 shadow-card">
                <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-3">{t('pages.clientReportDetail.scoresBySection')}</p>
                <ul className="space-y-2 text-sm">
                  {sections.map((s, i) => (
                    <li key={i} className="flex items-center justify-between"><span className="font-medium">{s.name}</span><span className="tabular-nums">{s.score}% · <span className="text-muted-foreground">{t(`reports.bands.${String(s.band || '').toLowerCase()}`, { defaultValue: s.band })}</span></span></li>
                  ))}
                </ul>
              </Card>
            )}
            {competencies.length > 0 && (
              <Card className="p-6 shadow-card">
                <p className="text-xs uppercase tracking-wider font-semibold text-accent mb-3">{t('pages.clientReportDetail.scoresByCompetency')}</p>
                <ul className="space-y-2 text-sm">
                  {competencies.map((cc, i) => (
                    <li key={i} className="flex items-center justify-between"><span className="font-medium">{t(`reports.competencies.${String(cc.code || cc.name || '').toLowerCase()}`, { defaultValue: cc.name })}</span><span className="tabular-nums">{cc.score}% · <span className="text-muted-foreground">{t(`reports.bands.${String(cc.band || '').toLowerCase()}`, { defaultValue: cc.band })}</span></span></li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}

        {/* Strengths + recommendations */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-6 shadow-card">
            <p className="text-xs uppercase tracking-wider font-semibold text-success mb-3">{t('pages.clientReportDetail.strengths')}</p>
            <ul className="space-y-2 text-sm">{(c.strengths || []).map((x, i) => <li key={i} className="flex gap-2"><Sparkles className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" /> {x}</li>)}</ul>
          </Card>
          <Card className="p-6 shadow-card">
            <p className="text-xs uppercase tracking-wider font-semibold text-warning mb-3">{t('pages.clientReportDetail.developmentAreas')}</p>
            <ul className="space-y-2 text-sm">{(c.weaknesses || []).map((x, i) => <li key={i} className="flex gap-2"><ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" /> {x}</li>)}</ul>
          </Card>
        </div>

        {(c.recommendations?.length || c.training_suggestions?.length) > 0 && (
          <Card className="p-6 shadow-card">
            <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-3">{t('pages.clientReportDetail.recommendations')}</p>
            <ul className="space-y-2 text-sm">
              {(c.recommendations || []).map((x, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" /> {x}</li>)}
              {(c.training_suggestions || []).map((x, i) => <li key={`t${i}`} className="flex gap-2"><ArrowRight className="h-3.5 w-3.5 text-info shrink-0 mt-0.5" /> {x}</li>)}
            </ul>
          </Card>
        )}

        {c.consultant_notes && (
          <Card className="p-6 shadow-card bg-muted/40">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t('pages.clientReportDetail.consultantNotes')}</p>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.consultant_notes}</p>
          </Card>
        )}
        </>
        )}
      </div>
    </>
  );
}
